<template>
  <el-dialog
    class="detail-dialog"
    :class="{'fullscreen':fullscreen}"
    :title="title"
    :visible.sync="visible"
    :width="width"
    :modal="modal"
    :lock-scroll="lockScroll"
    :close-on-click-modal="closeOnClickModal"
    :close-on-press-escape="closeOnPressEscape"
    :show-close="showClose"
    :before-close="handleBeforeClose"
    :center="center"
    :custom-class="customClass"
    :fullscreen="fullscreen"
    @open="handleOpen"
    @opened="handleOpened"
    @close="handleClose"
    @closed="handleClosed"
  >
    <!-- 头部插槽 -->
    <template v-if="$slots.title" slot="title">
      <slot name="title"></slot>
    </template>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container" v-loading="loading" :element-loading-text="loadingText">
    </div>

    <!-- 选项卡模式 -->
    <div v-else-if="internalIsTabMode && internalTabsData.length > 0" class="dialog-content">
      <slot name="before" :data="currentData"></slot>

      <el-tabs v-model="internalActiveTab">
        <el-tab-pane
          v-for="tab in internalTabsData"
          :key="tab.name"
          :label="tab.label"
          :name="tab.name"
        >
          <DetailContent
            :data="tab.data"
            :columns="tab.columns"
            :column="column"
            :border="border"
            :size="size"
          >
            <!-- 传递所有插槽到内部组件 -->
            <template v-for="(slot, name) in $scopedSlots" :slot="name" slot-scope="scope">
              <slot :name="name" v-bind="scope"></slot>
            </template>
          </DetailContent>
        </el-tab-pane>
      </el-tabs>

      <slot name="after" :data="currentData"></slot>
    </div>

    <!-- 普通模式 -->
    <div v-else-if="currentData && Object.keys(currentData).length > 0" class="dialog-content">
      <el-tabs v-model="detailActiveTab">
        <el-tab-pane label="基本信息" name="base">
          <slot name="before" :data="currentData"></slot>
          <DetailContent
            :data="currentData"
            :columns="currentColumns"
            :column="column"
            :border="border"
            :size="size"
          >
            <template v-for="(slot, name) in $scopedSlots" :slot="name" slot-scope="scope">
              <slot :name="name" v-bind="scope"></slot>
            </template>
          </DetailContent>
          <slot name="after" :data="currentData"></slot>
        </el-tab-pane>
        <el-tab-pane label="树洞内容" name="content">
          <div style="background:#fff;min-height:200px;padding:24px 18px;font-size:16px;color:#333;white-space:pre-wrap;">
            {{ currentData.content }}
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 无数据状态 -->
    <div v-else class="no-data">
      <div class="empty-content">
        <i class="el-icon-document" style="font-size: 48px; color: #C0C4CC;"></i>
        <p style="margin: 10px 0; color: #909399;">{{ emptyText }}</p>
      </div>
    </div>

    <!-- 底部插槽 -->
    <template slot="footer">
      <slot name="footer" :data="currentData">
        <el-button @click="handleClose">{{ closeText }}</el-button>
      </slot>
    </template>
  </el-dialog>
</template>

<script>
import DetailContent from './DetailContent.vue';

export default {
  name: 'DetailDialog',
  components: {
    DetailContent
  },
  props: {
    // 弹窗标题
    title: {
      type: String,
      default: '详情'
    },
    // 弹窗宽度
    width: {
      type: String,
      default: '800px'
    },
    // 距离顶部距离
    top: {
      type: String,
      default: '15vh'
    },
    // 是否显示遮罩层
    modal: {
      type: Boolean,
      default: true
    },
    // 是否在 Dialog 出现时将 body 滚动锁定
    lockScroll: {
      type: Boolean,
      default: true
    },
    // 是否可以通过点击 modal 关闭 Dialog
    closeOnClickModal: {
      type: Boolean,
      default: true
    },
    // 是否可以通过按下 ESC 关闭 Dialog
    closeOnPressEscape: {
      type: Boolean,
      default: true
    },
    // 是否显示关闭按钮
    showClose: {
      type: Boolean,
      default: true
    },
    // 是否对头部和底部采用居中布局
    center: {
      type: Boolean,
      default: false
    },
    // Dialog 的自定义类名
    customClass: {
      type: String,
      default: ''
    },
    // 关闭按钮文本
    closeText: {
      type: String,
      default: '关闭'
    },
    // 空数据提示文本
    emptyText: {
      type: String,
      default: '暂无数据'
    },
    // 加载提示文本
    loadingText: {
      type: String,
      default: '加载中...'
    },
    // 数据源（直接传入数据时使用）
    data: {
      type: Object,
      default: () => ({})
    },
    // 列配置
    columns: {
      type: Array,
      default: () => []
    },
    // 接口地址
    api: {
      type: String,
      default: ''
    },
    // 接口参数
    params: {
      type: Object,
      default: () => ({})
    },
    // 请求方法
    method: {
      type: String,
      default: 'get'
    },
    // 数据返回格式处理函数
    dataFormat: {
      type: Function,
      default: null
    },
    // 每行显示的列数
    column: {
      type: Number,
      default: 2
    },
    // 是否显示边框
    border: {
      type: Boolean,
      default: true
    },
    // 尺寸
    size: {
      type: String,
      default: 'medium'
    },
    // 是否全屏
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      currentData: {},
      currentColumns: [],
      internalIsTabMode: false,
      internalTabsData: [],
      internalActiveTab: '',
      detailActiveTab: 'base',
    };
  },
  watch: {
    data: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.currentData = newVal;
        }
      },
      immediate: true,
      deep: true
    },
    columns: {
      handler(newVal) {
        this.currentColumns = newVal || [];
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    // 1. 普通模式 - 直接传入数据
    open(data = {}, columns = [], options = {}) {
      this.visible = true;
      this.internalIsTabMode = false;
      this.currentData = data;
      this.currentColumns = columns;
      this.detailActiveTab = 'base';
      // 处理选项
      if (options.title) this.title = options.title;
      if (options.width) this.width = options.width;
      // 显示loading效果
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 300);
    },

    // 2. 普通模式 - 通过API获取数据
    async openByApi(api, params = {}, columns = [], options = {}) {
      this.visible = true;
      this.internalIsTabMode = false;
      this.currentColumns = columns;
      this.loading = true;
      
      // 处理选项
      if (options.title) this.title = options.title;
      if (options.width) this.width = options.width;
      
      try {
        const { data: res } = await this.$get(api, { params });
        
        let responseData = res;
        if (res && res.data) {
          responseData = res.data;
        }
        
        // 如果有数据格式处理函数，使用它处理数据
        if (options.dataFormat && typeof options.dataFormat === 'function') {
          responseData = options.dataFormat(responseData);
        }
        
        this.currentData = responseData;
      } catch (error) {
        console.error('获取详情数据失败:', error);
        this.$message.error('获取详情数据失败');
        this.currentData = {};
      } finally {
        this.loading = false;
      }
    },

    // 3. 选项卡模式 - 直接传入数据
    openWithTabs(tabsData = [], options = {}) {
      this.visible = true;
      this.internalIsTabMode = true;
      this.internalTabsData = tabsData;
      this.internalActiveTab = tabsData.length > 0 ? tabsData[0].name : '';
      this.currentData = {};
      this.currentColumns = [];
      
      // 处理选项
      if (options.title) this.title = options.title;
      if (options.width) this.width = options.width;
      
      // 选项卡模式下，将第一个选项卡的数据作为当前数据（用于插槽）
      if (tabsData.length > 0) {
        this.currentData = tabsData[0].data || {};
      }
      
      // 显示loading效果
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 300);
    },

    // 4. 选项卡模式 - 通过API获取数据（推荐用法）
    async openTabsByApi(api, params = {}, tabsConfig = [], options = {}) {
      this.visible = true;
      this.internalIsTabMode = true;
      this.loading = true;
      
      // 处理选项
      if (options.title) this.title = options.title;
      if (options.width) this.width = options.width;
      
      try {
        const { data: res } = await this.$get(api, { params });
        
        let responseData = res;
        if (res && res.data) {
          responseData = res.data;
        }
        
        // 根据配置生成选项卡数据
        this.internalTabsData = tabsConfig.map(config => {
          let tabData = responseData;
          
          // 如果有预处理函数，使用它处理数据
          if (config.preProcessData && typeof config.preProcessData === 'function') {
            tabData = config.preProcessData(responseData);
          }
          
          return {
            label: config.label,
            name: config.name,
            columns: config.columns,
            data: tabData
          };
        });
        
        this.internalActiveTab = this.internalTabsData.length > 0 ? this.internalTabsData[0].name : '';
        
        // 设置当前数据（用于插槽）
        if (this.internalTabsData.length > 0) {
          this.currentData = this.internalTabsData[0].data || {};
        }
        
      } catch (error) {
        console.error('获取详情数据失败:', error);
        this.$message.error('获取详情数据失败');
        this.internalTabsData = [];
        this.currentData = {};
      } finally {
        this.loading = false;
      }
    },

    // 生命周期事件处理
    handleOpen() {
      this.$emit('open');
    },

    handleOpened() {
      this.$emit('opened');
    },

    handleClose() {
      this.visible = false;
      this.$emit('close');
    },

    handleClosed() {
      // 重置数据
      this.currentData = {};
      this.currentColumns = [];
      this.internalIsTabMode = false;
      this.internalTabsData = [];
      this.internalActiveTab = '';
      this.loading = false;
      this.detailActiveTab = 'base';
      
      this.$emit('closed');
    },

    handleBeforeClose(done) {
      this.$emit('before-close', done);
      if (done) {
        done();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.dialog-content {
  max-height: 70vh;
  overflow-y: auto;
}

.loading-container {
  position: relative;
  height: 200px;
}

.no-data {
  text-align: center;
  padding: 40px 0;
  
  .empty-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    i {
      margin-bottom: 16px;
    }
    
    p {
      font-size: 14px;
      line-height: 1.5;
    }
  }
}
.detail-dialog{
  :deep(.el-dialog){
    margin:0 auto !important;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    .el-dialog__header,.el-dialog__footer{
      flex:none;
      width: 100%;
      box-sizing: border-box;
    }
    .el-dialog__body{
      flex: 1 1 auto;
      overflow-y: auto;
      width: 100%;
    box-sizing: border-box;
    }
  }
  &.fullscreen{
    :deep(.el-dialog){
      height: 100vh;
      width: 100vw;
    }
  }
}
::v-deep .el-dialog__body {
  padding: 20px;
}

</style> 